import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Briefcase, Coffee, LogIn, RotateCcw, Timer, X } from 'lucide-react';
import { engagementLabel } from '../../utils/engagementLabel';
import Dialog from '../ui/Dialog';
import TaskProgressField, { type TaskProgressValue } from './TaskProgressField';
import { effectiveProgress } from './progressModel';
import { saveTaskProgress } from './saveTaskProgress';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { useCloseTicket } from '../home/useCloseTicket';
import { clockChoices } from '../attendance/ClockInChoice';
import { apiErrorMessage } from '../sales/shared';
import {
  registerTimerFlowHost, type BackToWorkFirstRequest, type ClockInFirstChoice, type ClockInFirstRequest, type StatusAfterStopRequest,
  type TimerSwitchChoice, type TimerSwitchRequest, type WorkEngagementRequest,
} from './timerFlow';
import { formatClock } from '../home/homeModel';
import { isAutoMidnightInstant } from '../attendance/autoCloseoutModel';
import { appLocale, formatDate } from '../../utils/format';
import { engagementAsk, warrantyTag } from './timerModel';

/** D-64 — how long the status circles stay after ⏸ (unless the pointer / focus is on them). */
const STATUS_AFTER_STOP_MS = 8000;
/** P-10e (D-82) — the bar is saved this long after it stops moving. */
const BAR_SAVE_DELAY_MS = 700;

/**
 * Mounted once (in `GlobalTimer`, for staff). Answers `askClockInFirst` and
 * `askTimerSwitch` with small dialogs, shows the status circles for a moment
 * after ⏸ (`showStatusAfterStop`, D-64) and runs `requestCloseAfterTimer`
 * through `useCloseTicket`.
 */
export default function TimerFlowHost() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ req: TimerSwitchRequest; answer: (c: TimerSwitchChoice | null) => void } | null>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [clockAsk, setClockAsk] = useState<{ req: ClockInFirstRequest; answer: (c: ClockInFirstChoice | null) => void } | null>(null);
  const clockPrimaryRef = useRef<HTMLButtonElement>(null);
  const clockAskRef = useRef(clockAsk);
  clockAskRef.current = clockAsk;
  // D-98 — "את/ה בהפסקה. לסיים אותה ולהתחיל?"
  const [breakAsk, setBreakAsk] = useState<{ req: ClockInFirstRequest; answer: (end: boolean) => void } | null>(null);
  const breakPrimaryRef = useRef<HTMLButtonElement>(null);
  const breakAskRef = useRef(breakAsk);
  breakAskRef.current = breakAsk;
  // D-129 / QX-59 — "יצאת היום ב-13:00. לחזור לעבודה ולהתחיל את '...'?"
  const [backAsk, setBackAsk] = useState<{ req: BackToWorkFirstRequest; answer: (back: boolean) => void } | null>(null);
  const backPrimaryRef = useRef<HTMLButtonElement>(null);
  const backAskRef = useRef(backAsk);
  backAskRef.current = backAsk;
  // D-146 (Q-98) — "לאיזו התקשרות לרשום את השעות?" (a ticket opened without one).
  const [engAsk, setEngAsk] = useState<{ req: WorkEngagementRequest; answer: (id: string | null) => void } | null>(null);
  const [engChoice, setEngChoice] = useState('');
  const engAskRef = useRef(engAsk);
  engAskRef.current = engAsk;

  const closer = useCloseTicket({
    onCommitted: (id) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });
  const closeRequest = closer.request;
  const closeRef = useRef(closeRequest);
  closeRef.current = closeRequest;

  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  // D-64 — after ⏸: the task's status circles, for a moment; a change is optional.
  // P-10e (D-82) — and its progress bar under them, synced with the circles.
  const [afterStop, setAfterStop] = useState<(StatusAfterStopRequest & { value: TaskProgressValue; saved: TaskProgressValue }) | null>(null);
  const [holdAfterStop, setHoldAfterStop] = useState(false);
  useEffect(() => {
    if (!afterStop || holdAfterStop) return;
    const id = window.setTimeout(() => setAfterStop(null), STATUS_AFTER_STOP_MS);
    return () => window.clearTimeout(id);
  }, [afterStop, holdAfterStop]);
  const statusMutation = useMutation({
    mutationFn: async (v: { ticketId: string; before: TaskProgressValue; after: TaskProgressValue }) => {
      const r = await saveTaskProgress(v.ticketId, v.before, v.after);
      // 🟢 from the panel: saved as before (P-10a); the server stops the timers on it (D-65).
      if (r.complete) await api.updateTicketStatus(v.ticketId, 'completed');
    },
    onSuccess: (_r, v) => {
      setAfterStop((cur) => (cur && cur.ticketId === v.ticketId ? { ...cur, status: v.after.status, saved: v.after } : cur));
      queryClient.invalidateQueries({ queryKey: ['ticket', v.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
      queryClient.invalidateQueries({ queryKey: ['milestone'] });
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('ticketDetail.statusChangeFailed'))),
  });
  // D-82 — the bar saves once it rests (not on every 10% step).
  const barPending = !!afterStop && afterStop.value.status === afterStop.saved.status
    && afterStop.value.progress !== afterStop.saved.progress;
  const saveBar = () => {
    if (afterStop && barPending) statusMutation.mutate({ ticketId: afterStop.ticketId, before: afterStop.saved, after: afterStop.value });
  };
  const saveBarRef = useRef(saveBar);
  saveBarRef.current = saveBar;
  useEffect(() => {
    if (!barPending || statusMutation.isPending) return;
    const id = window.setTimeout(() => saveBarRef.current(), BAR_SAVE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [barPending, afterStop?.value.progress, statusMutation.isPending]);

  useEffect(() => registerTimerFlowHost({
    ask: (req, answer) => {
      // A second request while one is open: the first one is cancelled.
      pendingRef.current?.answer(null);
      const next = { req, answer };
      pendingRef.current = next;
      setPending(next);
    },
    askClockIn: (req, answer) => {
      clockAskRef.current?.answer(null);
      const next = { req, answer };
      clockAskRef.current = next;
      setClockAsk(next);
    },
    askEndBreak: (req, answer) => {
      breakAskRef.current?.answer(false);
      const next = { req, answer };
      breakAskRef.current = next;
      setBreakAsk(next);
    },
    askBackToWork: (req, answer) => {
      backAskRef.current?.answer(false);
      const next = { req, answer };
      backAskRef.current = next;
      setBackAsk(next);
    },
    askEngagement: (req, answer) => {
      engAskRef.current?.answer(null);
      const next = { req, answer };
      engAskRef.current = next;
      // D-302 (QX-1310 / QX-1307): nothing chosen in advance when a warranty question is asked.
      setEngChoice(engagementAsk(req.engagements).preselect ? (req.engagements[0]?.id ?? '') : '');
      setEngAsk(next);
    },
    close: (item) => { void closeRef.current(item); },
    statusAfterStop: (req) => {
      setHoldAfterStop(false);
      const value = { status: req.status, progress: effectiveProgress(req.status, req.progress) };
      setAfterStop({ ...req, value, saved: value });
    },
  }), []);

  // Unmounting with a question open (logout): cancel it, never start silently.
  useEffect(() => () => {
    pendingRef.current?.answer(null);
    clockAskRef.current?.answer(null);
    breakAskRef.current?.answer(false);
    backAskRef.current?.answer(false);
    engAskRef.current?.answer(null);
  }, []);

  const replyBack = (back: boolean) => {
    backAsk?.answer(back);
    backAskRef.current = null;
    setBackAsk(null);
  };
  const backTime = backAsk?.req.clockedOutAt
    ? (isAutoMidnightInstant(backAsk.req.clockedOutAt) ? '00:00' : formatClock(backAsk.req.clockedOutAt, appLocale(i18n.language)))
    : '';

  const replyEngagement = (id: string | null) => {
    engAsk?.answer(id);
    engAskRef.current = null;
    setEngAsk(null);
  };

  const replyBreak = (end: boolean) => {
    breakAsk?.answer(end);
    breakAskRef.current = null;
    setBreakAsk(null);
  };

  const replyClock = (choice: ClockInFirstChoice | null) => {
    clockAsk?.answer(choice);
    clockAskRef.current = null;
    setClockAsk(null);
  };
  const places = clockChoices();

  const reply = (choice: TimerSwitchChoice | null) => {
    pending?.answer(choice);
    pendingRef.current = null;
    setPending(null);
  };

  const others = pending?.req.others ?? [];
  const one = others.length === 1 ? others[0] : null;
  const runningTitle = one ? (one.ticket_title || (one.ticket_number ? `#${one.ticket_number}` : '')) : '';
  const nextTitle = pending?.req.task.title ?? '';
  const stopLabel = one
    ? t('timer.switch.stopAndSwitch', { title: runningTitle })
    : t('timer.switch.stopAllAndSwitch', { count: others.length });

  return (
    <>
      {/* "להחתים כניסה ולהתחיל?" (documentation brief, part 1): asked before the switch question. */}
      <Dialog
        open={!!clockAsk}
        onClose={() => replyClock(null)}
        title={t('timer.clockIn.title')}
        icon={<LogIn size={20} strokeWidth={1.75} className="rtl-mirror shrink-0 text-accent-text" aria-hidden />}
        size="sm"
        initialFocus={clockPrimaryRef}
        panelProps={{ 'data-timer-clock-in': '' }}
        footer={clockAsk ? (
          // D-66: clock in (office / home) and start, or cancel — no "timer only".
          <div className="flex w-full flex-col gap-2">
            {/* The last place used first, as in every clock-in choice. */}
            <div role="group" aria-label={t('home.attendance.whereToday')} className="flex flex-col gap-2">
              {places.map(({ value, Icon }, i) => (
                <button
                  key={value}
                  ref={i === 0 ? clockPrimaryRef : undefined}
                  type="button"
                  className={`btn ${i === 0 ? 'btn-primary' : 'btn-secondary'} w-full`}
                  onClick={() => replyClock(value)}
                  data-clock-location={value}
                >
                  <Icon size={16} strokeWidth={1.75} aria-hidden />
                  {t(value === 'home' ? 'timer.clockIn.homeAndStart' : 'timer.clockIn.officeAndStart')}
                </button>
              ))}
            </div>
            <button type="button" className="btn btn-ghost w-full" onClick={() => replyClock(null)}>
              {t('common.cancel')}
            </button>
          </div>
        ) : undefined}
      >
        {clockAsk && (
          <div className="space-y-2 text-sm text-text-2">
            <p>{clockAsk.req.autoClockout ? t('timer.clockIn.autoClockout') : t('timer.clockIn.notOnShift')}</p>
            <p>{t('timer.switch.next')} <bdi className="font-medium text-text">{clockAsk.req.task.title}</bdi></p>
          </div>
        )}
      </Dialog>
      {/* D-129 / QX-59 — clocked out today: ask before going back to work. */}
      <Dialog
        open={!!backAsk}
        onClose={() => replyBack(false)}
        title={t('timer.backToWork.title')}
        icon={<RotateCcw size={20} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden />}
        size="sm"
        initialFocus={backPrimaryRef}
        panelProps={{ 'data-timer-back-to-work': '' }}
        footer={backAsk ? (
          <div className="flex w-full flex-col gap-2">
            <button ref={backPrimaryRef} type="button" className="btn btn-primary w-full" onClick={() => replyBack(true)}>
              {t('timer.backToWork.backAndStart')}
            </button>
            <button type="button" className="btn btn-ghost w-full" onClick={() => replyBack(false)}>
              {t('common.cancel')}
            </button>
          </div>
        ) : undefined}
      >
        {backAsk && (
          <p className="text-sm text-text-2">
            {backTime
              ? t('timer.backToWork.body', { time: backTime, title: backAsk.req.task.title })
              : t('timer.backToWork.bodyNoTime', { title: backAsk.req.task.title })}
          </p>
        )}
      </Dialog>
      {/* D-146 (Q-98) — a ticket without an engagement: on which one to log the hours. */}
      <Dialog
        open={!!engAsk}
        onClose={() => replyEngagement(null)}
        title={t('timer.engagement.title')}
        icon={<Briefcase size={20} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden />}
        size="sm"
        panelProps={{ 'data-timer-engagement': '' }}
        footer={engAsk ? (
          <div className="flex w-full flex-col gap-2">
            {engAsk.req.engagements.length > 0 && (
              <button type="button" className="btn btn-primary w-full" disabled={!engChoice} onClick={() => replyEngagement(engChoice || null)}>
                {t(engAsk.req.mode === 'log' ? 'timer.engagement.log' : 'timer.engagement.start')}
              </button>
            )}
            <button type="button" className="btn btn-ghost w-full" onClick={() => replyEngagement(null)}>
              {t('common.cancel')}
            </button>
          </div>
        ) : undefined}
      >
        {engAsk && (
          <div className="space-y-3 text-sm text-text-2">
            {/* D-302 QX-1310 / QX-1307 — "is it about the project <name> (warranty) or the bank?" / "the warranty ended". */}
            <p>{(() => {
              const ask = engagementAsk(engAsk.req.engagements);
              if (ask.key) return t(ask.key, { project: ask.project, service: t(`timer.engagement.serviceOf.${ask.service}`) });
              return engAsk.req.task.title ? t('timer.engagement.body', { title: engAsk.req.task.title }) : t('timer.engagement.bodyNoTitle');
            })()}</p>
            {engAsk.req.engagements.length === 0 ? (
              <p role="alert" className="text-danger-700">{t('timer.engagement.none')}</p>
            ) : (
              <fieldset className="space-y-1.5">
                <legend className="sr-only">{t('timer.engagement.title')}</legend>
                {engAsk.req.engagements.map((e) => (
                  <label key={e.id} className="flex min-h-touch cursor-pointer items-center gap-2 rounded-md px-2 hover:bg-surface-subtle">
                    <input type="radio" name="timer-engagement" className="h-4 w-4 shrink-0 accent-primary-700" checked={engChoice === e.id} onChange={() => setEngChoice(e.id)} />
                    <span className="flex min-w-0 flex-col items-start gap-0.5 py-1">
                      <bdi className="text-text">{engagementLabel(e.name, e.type, t)}</bdi>
                      {(() => {
                        // D-300 / D-303 — a project in its warranty period (its "אחריות ללקוח" date has not passed): its hours are not billed.
                        const tag = warrantyTag(e);
                        return tag ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="badge badge-gray text-xs" data-warranty-tag="">
                              {t(tag.key, { date: tag.until ? formatDate(tag.until) : '' })}
                            </span>
                          </span>
                        ) : null;
                      })()}
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
          </div>
        )}
      </Dialog>
      {/* D-98 — no timer during a break: end it and start, or cancel. */}
      <Dialog
        open={!!breakAsk}
        onClose={() => replyBreak(false)}
        title={t('timer.onBreak.title')}
        icon={<Coffee size={20} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden />}
        size="sm"
        initialFocus={breakPrimaryRef}
        panelProps={{ 'data-timer-on-break': '' }}
        footer={breakAsk ? (
          <div className="flex w-full flex-col gap-2">
            <button ref={breakPrimaryRef} type="button" className="btn btn-primary w-full" onClick={() => replyBreak(true)}>
              {t('timer.onBreak.endAndStart')}
            </button>
            <button type="button" className="btn btn-ghost w-full" onClick={() => replyBreak(false)}>
              {t('common.cancel')}
            </button>
          </div>
        ) : undefined}
      >
        {breakAsk && (
          <div className="space-y-2 text-sm text-text-2">
            <p>{t('timer.onBreak.body')}</p>
            <p>{t('timer.switch.next')} <bdi className="font-medium text-text">{breakAsk.req.task.title}</bdi></p>
          </div>
        )}
      </Dialog>
      <Dialog
        open={!!pending}
        onClose={() => reply(null)}
        title={t('timer.switch.title')}
        icon={<Timer size={20} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden />}
        size="sm"
        initialFocus={primaryRef}
        footer={pending ? (
          // A decision with two real answers: stacked, the default on top (the
          // labels carry a task title, so a row would wrap unevenly).
          <div className="flex w-full flex-col gap-2">
            <button
              ref={primaryRef}
              type="button"
              className="btn btn-primary w-full min-w-0"
              onClick={() => reply('switch')}
              aria-label={stopLabel}
            >
              <span className="truncate">{stopLabel}</span>
            </button>
            <button type="button" className="btn btn-secondary w-full" onClick={() => reply('both')}>
              {one ? t('timer.switch.runBoth') : t('timer.switch.runAll')}
            </button>
            <button type="button" className="btn btn-ghost w-full" onClick={() => reply(null)}>
              {t('common.cancel')}
            </button>
          </div>
        ) : undefined}
      >
        {pending && (
          <div className="space-y-2 text-sm text-text-2">
            <p>
              {one
                ? <>{t('timer.switch.runningOne')} <bdi className="font-medium text-text">{runningTitle}</bdi>.</>
                : t('timer.switch.runningMany', { count: others.length })}
            </p>
            <p>{t('timer.switch.next')} <bdi className="font-medium text-text">{nextTitle}</bdi></p>
            <p className="text-xs">{t('timer.switch.hint')}</p>
          </div>
        )}
      </Dialog>
      {afterStop && (
        // D-64: a light, non-blocking panel — the stop already happened.
        <div
          role="status"
          aria-live="polite"
          data-timer-after-stop=""
          onMouseEnter={() => setHoldAfterStop(true)}
          onMouseLeave={() => setHoldAfterStop(false)}
          onFocus={() => setHoldAfterStop(true)}
          onBlur={() => setHoldAfterStop(false)}
          className="mat-float fixed bottom-24 start-4 end-4 z-popover mx-auto max-w-md rounded-xl p-3 sm:start-auto sm:end-6 sm:mx-0"
        >
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <p className="min-w-0 text-sm text-text">
              {t('timer.paused')} <bdi className="font-medium">{afterStop.title}</bdi>
            </p>
            <button type="button" onClick={() => { saveBar(); setAfterStop(null); }} aria-label={t('shell.close')} className="btn-icon -m-1.5 text-text-2 hover:text-text">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <p className="mb-1 text-xs text-text-2">{t('timer.statusAfterStopHint')}</p>
          <TaskProgressField
            value={afterStop.value}
            size="sm"
            disabled={statusMutation.isPending}
            title={afterStop.title}
            onChange={(next) => {
              setHoldAfterStop(true);
              setAfterStop((cur) => (cur ? { ...cur, value: next } : cur));
              // A circle saves at once; the bar saves when it rests (below).
              if (next.status !== afterStop.value.status) {
                statusMutation.mutate({ ticketId: afterStop.ticketId, before: afterStop.saved, after: next });
              }
            }}
          />
        </div>
      )}
      {closer.dialogs}
    </>
  );
}
